import * as React from 'react';
import { TCategoriesMenu } from './CategoriesMenu.types';
import { PortalToID } from '../../../../../utils/dom';
import { CategoryRow } from './components/CategoryRow/CategoryRow';
import { Subcategory } from './components/Subcategory/Subcategory';
import './CategoriesMenu.styled.scss';
import classNames from 'classnames';
import { CATEGORIES_MENU_CONFIG } from './config';
import { IconButton } from 'components';
import MenuIcon from '@mui/icons-material/Menu';
import { Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useLocation } from 'react-router';
import { APP_ROUTES } from '../../../../../constants/routes';
import { useFilters } from '../../../../../store/contexts/FiltersContext/FiltersContext.provider';
import { useDeviceType } from 'hooks/useDeviceType';

export const CategoriesMenu: TCategoriesMenu = () => {
  const location = useLocation();
  const { isMobile, isTablet } = useDeviceType();
  const { openFilters } = useFilters();
  const [isCategoriesMenuOpen, setCategoriesMenuOpen] = React.useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = React.useState<
    string | null
  >(null);

  const menuRef = React.useRef<HTMLDivElement>(null);
  const buttonRef = React.useRef<HTMLDivElement>(null);

  const isOnProductsPage = location.pathname === APP_ROUTES.PHONE_CASES;

  const handleCategoriesToggle = () => {
    // On mobile and products page, open filters instead
    if (isMobile && isOnProductsPage) {
      openFilters();
      return;
    }

    setCategoriesMenuOpen((prev) => !prev);
    if (!isCategoriesMenuOpen) {
      setSelectedCategoryId(null);
    }
  };

  const handleCategoryHover = (categoryId: string) => {
    setSelectedCategoryId(categoryId);
  };

  const handleCategoryLeave = () => {
    // Don't clear immediately - let subcategories area handle it
  };

  const handleMenuLeave = () => {
    // Clear selection when leaving the entire menu
    setSelectedCategoryId(null);
  };

  const handleCategoryClick = React.useCallback(() => {
    setCategoriesMenuOpen(false);
    setSelectedCategoryId(null);
  }, []);

  // Handle click outside to close menu
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        menuRef.current &&
        !menuRef.current.contains(target) &&
        buttonRef.current &&
        !buttonRef.current.contains(target)
      ) {
        setCategoriesMenuOpen(false);
        setSelectedCategoryId(null);
      }
    };

    if (isCategoriesMenuOpen) {
      // Use a small delay to avoid immediate closure when opening
      setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 0);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCategoriesMenuOpen]);

  const selectedCategory = selectedCategoryId
    ? CATEGORIES_MENU_CONFIG.find((cat) => cat.id === selectedCategoryId)
    : null;

  return (
    <div>
      {isMobile || isTablet ? (
        <>
          <IconButton
            ref={buttonRef as unknown as React.RefObject<HTMLButtonElement>}
            onClick={handleCategoriesToggle}
            className="categories-menu__hamburger"
            color="primary"
          >
            <MenuIcon fontSize="large" />
          </IconButton>
          {isCategoriesMenuOpen && (
            <PortalToID id="app_header">
              <div
                className="categories-menu categories-menu--mobile"
                ref={menuRef}
              >
                <div className="categories-menu__mobile-content">
                  {CATEGORIES_MENU_CONFIG.map((category) => (
                    <Accordion
                      key={category.id}
                      className="categories-menu__accordion"
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        className="categories-menu__accordion-summary"
                      >
                        {category.displayName}
                      </AccordionSummary>
                      <AccordionDetails className="categories-menu__accordion-details">
                        {category.subCategories && (
                          <Subcategory
                            subcategories={category.subCategories}
                            categoryId={category.id}
                            onCategoryClick={handleCategoryClick}
                          />
                        )}
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </div>
              </div>
            </PortalToID>
          )}
        </>
      ) : (
        <>
          <div
            ref={buttonRef}
            onClick={handleCategoriesToggle}
            className={classNames(
              'caption',
              'header__main-content__links__link',
              'categories-menu__categories-button'
            )}
          >
            Категорії
          </div>

          {isCategoriesMenuOpen && (
            <PortalToID id="app_header">
              <div
                className="categories-menu"
                ref={menuRef}
                onMouseLeave={handleMenuLeave}
              >
                <div className="categories-menu__main-categories">
                  {CATEGORIES_MENU_CONFIG.map((category) => (
                    <CategoryRow
                      key={category.id}
                      category={category}
                      isSelected={selectedCategoryId === category.id}
                      onMouseEnter={() => handleCategoryHover(category.id)}
                      onMouseLeave={handleCategoryLeave}
                      onCategoryClick={handleCategoryClick}
                    />
                  ))}
                </div>
                {selectedCategoryId && selectedCategory && (
                  <div
                    className={classNames('categories-menu__subcategories', {
                      'categories-menu__subcategories--visible':
                        selectedCategoryId !== null,
                    })}
                    onMouseEnter={() =>
                      handleCategoryHover(selectedCategory.id)
                    }
                    onMouseLeave={handleCategoryLeave}
                  >
                    <Subcategory
                      subcategories={selectedCategory.subCategories}
                      categoryId={selectedCategory.id}
                      onCategoryClick={handleCategoryClick}
                    />
                  </div>
                )}
              </div>
            </PortalToID>
          )}
        </>
      )}
    </div>
  );
};
